import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, PhoneCall, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toCents } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CANALES = ["whatsapp", "sms", "voz"];
const MODALIDADES = ["semanal", "quincenal", "mensual"];
const CONTRATACIONES = ["por_contrato", "por_hora"];

const empty = {
  nombre: "", empresa: "", qbo_nombre_proveedor: "", telefono: "", email: "", especialidades: [],
  tarifa: "", canal_preferido: "whatsapp", canales_orden: ["whatsapp", "sms", "voz"], sabe_leer: true,
  modalidad_liquidacion: "quincenal", disponibilidad: "completa", idioma: "es",
  zonas: "", notas: "",
  tipo_contratacion: "por_contrato", capacidad_diaria: "3", dias_pago_post_trabajo: "",
};

export const SubDialog = ({ open, onOpenChange, sub, onDone }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  // Fix K (2026-09-28): más de 100 servicios en una caja de 150px: se filtra escribiendo.
  const [espFiltro, setEspFiltro] = useState("");

  const { data: serviceTypes } = useQuery({
    queryKey: ["service-types"],
    enabled: open,
    queryFn: async () => (await api.get("/service-types")).data,
  });

  useEffect(() => {
    if (!open) return;
    setForm(
      sub
        ? {
            nombre: sub.nombre || "", empresa: sub.empresa || "", telefono: sub.telefono || "",
            qbo_nombre_proveedor: sub.qbo_nombre_proveedor || "",
            email: sub.email || "", especialidades: sub.especialidades || [],
            tarifa: sub.tarifa_promedio_cents ? (sub.tarifa_promedio_cents / 100).toFixed(2) : "",
            canal_preferido: sub.canal_preferido || "whatsapp",
            canales_orden: sub.canales_orden?.length
              ? [...sub.canales_orden, ...CANALES.filter((c) => !sub.canales_orden.includes(c))]
              : [sub.canal_preferido || "whatsapp",
                 ...CANALES.filter((c) => c !== (sub.canal_preferido || "whatsapp"))],
            sabe_leer: sub.sabe_leer !== false,
            modalidad_liquidacion: sub.modalidad_liquidacion || "quincenal",
            disponibilidad: sub.disponibilidad || "completa", idioma: sub.idioma || "es",
            zonas: (sub.zonas || []).join(", "), notas: sub.notas || "",
            tipo_contratacion: sub.tipo_contratacion || "por_contrato",
            capacidad_diaria: sub.capacidad_diaria != null ? String(sub.capacidad_diaria) : "3",
            dias_pago_post_trabajo: sub.dias_pago_post_trabajo != null ? String(sub.dias_pago_post_trabajo) : "",
          }
        : empty
    );
  }, [open, sub]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        nombre: form.nombre.trim(), empresa: form.empresa.trim() || null,
        qbo_nombre_proveedor: form.qbo_nombre_proveedor.trim() || null,
        telefono: form.telefono.trim(), email: form.email.trim() || null,
        especialidades: form.especialidades,
        tarifa_promedio_cents: toCents(form.tarifa),
        canal_preferido: form.sabe_leer ? form.canal_preferido : "voz",
        canales_orden: form.sabe_leer ? form.canales_orden : ["voz"],
        sabe_leer: form.sabe_leer,
        modalidad_liquidacion: form.modalidad_liquidacion,
        disponibilidad: form.disponibilidad, idioma: form.idioma,
        zonas: form.zonas.split(",").map((z) => z.trim()).filter(Boolean),
        notas: form.notas.trim() || null,
        tipo_contratacion: form.tipo_contratacion,
        capacidad_diaria: Number(form.capacidad_diaria) || 3,
        dias_pago_post_trabajo: form.dias_pago_post_trabajo ? Number(form.dias_pago_post_trabajo) : null,
      };
      return sub
        ? (await api.patch(`/subs/${sub.id}`, payload)).data
        : (await api.post("/subs", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subs"] });
      qc.invalidateQueries({ queryKey: ["sub", sub?.id] });
      toast.success(t("app.saved"));
      onDone?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const toggleEsp = (id) =>
    set(
      "especialidades",
      form.especialidades.includes(id)
        ? form.especialidades.filter((x) => x !== id)
        : [...form.especialidades, id]
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sub-dialog" className="max-h-[92vh] max-w-[620px] overflow-y-auto rounded-[10px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {sub ? t("subs.edit") : t("subs.new")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3.5">
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("app.name")} *</Label>
            <Input data-testid="sub-name-input" value={form.nombre}
              onChange={(e) => set("nombre", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("subs.company")}</Label>
            <Input data-testid="sub-company-input" value={form.empresa}
              onChange={(e) => set("empresa", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>
          {/* Fix M (2026-09-28): para que el costo del sub vaya al proveedor que ya existe en QuickBooks. */}
          <div className="col-span-2">
            <Label className="text-[12px] text-ink-tertiary">{t("subs.qbo_vendor_name")}</Label>
            <Input data-testid="sub-qbo-vendor-input" value={form.qbo_nombre_proveedor}
              onChange={(e) => set("qbo_nombre_proveedor", e.target.value)}
              placeholder={t("subs.qbo_vendor_name_ph")}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("app.phone")} *</Label>
            <Input data-testid="sub-phone-input" value={form.telefono} placeholder="(415) 555-1234"
              onChange={(e) => set("telefono", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("app.email")}</Label>
            <Input data-testid="sub-email-input" value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>

          <div className="col-span-2">
            <Label className="text-[12px] text-ink-tertiary">{t("subs.specialties")}</Label>
            <Input data-testid="sub-specialties-search" value={espFiltro}
              onChange={(e) => setEspFiltro(e.target.value)}
              placeholder={t("wo.form.service_search")}
              className="mt-1 h-8 rounded-[6px] text-[12.5px]" />
            <div data-testid="sub-specialties" className="mt-1.5 grid max-h-[180px] grid-cols-2 gap-x-4 gap-y-1.5 overflow-y-auto rounded-[6px] border border-line p-2.5">
              {(serviceTypes?.items || [])
                .filter((s) => form.especialidades.includes(s.id)
                  || s.nombre.toLowerCase().includes(espFiltro.trim().toLowerCase()))
                .map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-[12.5px] text-ink">
                  <Checkbox
                    data-testid={`sub-specialty-${s.id}`}
                    checked={form.especialidades.includes(s.id)}
                    onCheckedChange={() => toggleEsp(s.id)}
                  />
                  {s.nombre}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("subs.rate")}</Label>
            <Input data-testid="sub-rate-input" value={form.tarifa} inputMode="decimal" placeholder="0.00"
              onChange={(e) => set("tarifa", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-right text-[13.5px] tabular-nums" />
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("subs.settlement")}</Label>
            <Select value={form.modalidad_liquidacion} onValueChange={(v) => set("modalidad_liquidacion", v)}>
              <SelectTrigger data-testid="sub-settlement-select" className="mt-1 h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODALIDADES.map((m) => (
                  <SelectItem key={m} value={m}>{t(`subs.settlements.${m}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 rounded-[8px] border border-line bg-surface-sunken p-3">
            <label className="flex items-center justify-between gap-3">
              <span className="text-[12.5px] font-medium text-ink">{t("subs.can_read")}</span>
              <Switch
                data-testid="sub-can-read-switch"
                checked={form.sabe_leer}
                onCheckedChange={(v) => {
                  set("sabe_leer", v);
                  if (!v) set("canal_preferido", "voz");
                }}
              />
            </label>
            <p className="mt-1 text-[11.5px] text-ink-tertiary">{t("subs.can_read_help")}</p>

            <div className="mt-3">
              <Label className="text-[12px] text-ink-tertiary">{t("subs.channel")}</Label>
              <Select
                value={form.sabe_leer ? form.canal_preferido : "voz"}
                onValueChange={(v) => setForm((f) => ({
                  ...f, canal_preferido: v, canales_orden: [v, ...f.canales_orden.filter((c) => c !== v)],
                }))}
                disabled={!form.sabe_leer}
              >
                <SelectTrigger data-testid="sub-channel-select" className="mt-1 h-9 rounded-[6px] bg-white text-[13.5px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CANALES.map((c) => (
                    <SelectItem key={c} value={c}>{t(`channels.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Fix N (2026-09-28): si el primer canal falla, se prueba el 2° y después el 3°. */}
            {form.sabe_leer && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11.5px] text-ink-secondary"
                data-testid="sub-channel-order">
                <span>{t("subs.channel_fallback")}</span>
                {form.canales_orden.slice(1).map((c, i) => (
                  <span key={c} className="rounded-[4px] border border-line bg-white px-1.5 py-[1px]">
                    {i + 2}° {t(`channels.${c}`)}
                  </span>
                ))}
                <button type="button" data-testid="sub-channel-order-swap"
                  onClick={() => setForm((f) => ({
                    ...f, canales_orden: [f.canales_orden[0], f.canales_orden[2], f.canales_orden[1]],
                  }))}
                  className="rounded-[4px] border border-line bg-white px-1.5 py-[1px] text-brand hover:bg-surface-sunken">
                  {t("subs.channel_swap")}
                </button>
              </div>
            )}
            {!form.sabe_leer && (
              <p data-testid="sub-voice-warning" className="mt-2 flex items-start gap-1.5 text-[11.5px] font-medium text-state-warn">
                <PhoneCall className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                {t("subs.voice_forced")}
              </p>
            )}
          </div>

          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("subs.availability")}</Label>
            <Select value={form.disponibilidad} onValueChange={(v) => set("disponibilidad", v)}>
              <SelectTrigger data-testid="sub-availability-select" className="mt-1 h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["completa", "media", "fines_de_semana", "no_disponible"].map((d) => (
                  <SelectItem key={d} value={d}>{t(`subs.availabilities.${d}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("app.language")}</Label>
            <Select value={form.idioma} onValueChange={(v) => set("idioma", v)}>
              <SelectTrigger data-testid="sub-language-select" className="mt-1 h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[12px] text-ink-tertiary">{t("subs.zones")}</Label>
            <Input data-testid="sub-zones-input" value={form.zonas} placeholder="San Francisco, Oakland"
              onChange={(e) => set("zonas", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("subs.contract_type")}</Label>
            <Select value={form.tipo_contratacion} onValueChange={(v) => set("tipo_contratacion", v)}>
              <SelectTrigger data-testid="sub-contract-type-select" className="mt-1 h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTRATACIONES.map((c) => (
                  <SelectItem key={c} value={c}>{t(`subs.contract_types.${c}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("subs.daily_capacity")}</Label>
            <Input data-testid="sub-daily-capacity-input" type="number" value={form.capacidad_diaria}
              onChange={(e) => set("capacidad_diaria", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>
          <div>
            <Label className="text-[12px] text-ink-tertiary">{t("subs.pay_days_after")}</Label>
            <Input data-testid="sub-pay-days-after-input" type="number" value={form.dias_pago_post_trabajo}
              placeholder="—"
              onChange={(e) => set("dias_pago_post_trabajo", e.target.value)}
              className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
          </div>
          <div className="col-span-2">
            <Label className="text-[12px] text-ink-tertiary">{t("app.notes")}</Label>
            <Textarea data-testid="sub-notes-input" value={form.notas} rows={2}
              onChange={(e) => set("notas", e.target.value)}
              className="mt-1 rounded-[6px] text-[13.5px]" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 rounded-[6px] text-[13px]">
            {t("app.cancel")}
          </Button>
          <Button
            data-testid="sub-save-button"
            disabled={!form.nombre.trim() || !form.telefono.trim() || save.isPending}
            onClick={() => save.mutate()}
            className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
          >
            {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
